import { useState } from 'react';
import { getCopy } from '../utils/copy';
import { saveDraft, takeDraft } from './retryDraft';

/**
 * 多选题(params.type === 'multi'):复选卡片 + 「就这些」确认按钮。
 *
 * 点一下选中、再点取消;至少选一项才能确认。answer 是选中的 id 数组(按选项顺序),
 * 后端按集合比较、与顺序无关。答错只给温和提示、不指出哪一项不对,并保留上次的选择。
 */
function MultiPuzzle({ puzzle, onAnswer, feedback, disabled, ageBand }) {
  const { prompt, target, choices } = puzzle.params;
  const [selected, setSelected] = useState(() => takeDraft(puzzle.id, feedback, []));

  const toggle = (id) =>
    setSelected((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));
  const submit = () => {
    const answer = choices.map((c) => c.id).filter((id) => selected.includes(id));
    saveDraft(puzzle.id, answer);
    onAnswer(answer);
  };

  return (
    <div className="flex w-full flex-col items-center gap-5 rounded-2xl border-2 border-black bg-white p-4 shadow-brutal sm:p-8">
      <p className="text-lg font-bold text-gray-800">{puzzle.title}</p>
      {prompt && (
        <p data-testid="puzzle-prompt" className="-mt-3 break-words text-center text-lg font-medium text-gray-700">
          {prompt}
        </p>
      )}
      <p className="w-full whitespace-pre-line break-words text-left text-xl leading-relaxed">{target}</p>
      <p className="-mt-2 text-base text-gray-600">可能不止一个哦</p>
      {feedback && !feedback.correct && (
        <p role="alert" className="w-full text-center text-base font-medium text-orange-700">
          {getCopy(ageBand).retryHint}
        </p>
      )}

      <div className="flex w-full flex-col gap-3" role="group" aria-label="选项,可以多选">
        {choices.map((choice) => {
          const on = selected.includes(choice.id);
          return (
            <button
              key={choice.id}
              type="button"
              role="checkbox"
              aria-checked={on}
              data-testid={`choice-${choice.id}`}
              disabled={disabled}
              onClick={() => toggle(choice.id)}
              className={`flex min-h-[48px] w-full items-center gap-3 break-words rounded-xl border-2 border-black px-4 py-3
                          text-left text-base font-semibold leading-snug text-gray-800 shadow-brutal-sm transition
                          active:translate-y-0 active:shadow-none disabled:opacity-50
                          ${on ? 'bg-qiji-mint' : 'bg-amber-50'}`}
            >
              <span
                aria-hidden="true"
                className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-md border-2 border-black text-lg font-bold
                            ${on ? 'bg-white' : 'bg-white text-transparent'}`}
              >
                ✓
              </span>
              <span className="min-w-0 flex-1">{choice.label}</span>
            </button>
          );
        })}
      </div>

      <button
        type="button"
        data-testid="multi-submit"
        disabled={disabled || selected.length === 0}
        onClick={submit}
        className="min-h-[48px] w-full rounded-lg border-2 border-black bg-qiji-gold px-6 py-3 text-base font-bold text-gray-900
                   shadow-brutal-sm transition active:translate-y-0 active:shadow-none disabled:cursor-not-allowed disabled:opacity-50"
      >
        就这些
      </button>
    </div>
  );
}

export default MultiPuzzle;
